import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

const mainLinks = [
  { label: 'Startseite', path: '/' },
  { label: 'Über uns', path: '/ueber-uns' },
  { label: 'Leistungen', path: '/leistungen' },
  { label: 'Kontakt', path: '/kontakt' },
];

const moreLinks = [
  { label: 'Referenzen', path: '/referenzen', icon: 'ri-image-line' },
  { label: 'Stellenangebote', path: '/stellenangebote', icon: 'ri-briefcase-line' },
];

const allLinks = [...mainLinks, ...moreLinks];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (path: string) => location.pathname === path;
  const isMoreActive = moreLinks.some((l) => location.pathname === l.path);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || menuOpen ? 'bg-white shadow-sm' : 'bg-transparent'
      }`}
      role="banner"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center cursor-pointer shrink-0" aria-label="gowiTEC – Zur Startseite">
          <img
            src="https://public.readdy.ai/ai/img_res/14f1f06c-918d-4446-9087-9b10eac1e38e.png"
            alt="gowiTEC – Systemkomponenten + SPS-Technik"
            className="h-12 w-auto object-contain"
            loading="eager"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-7" aria-label="Hauptnavigation">
          {/* Hauptlinks */}
          {mainLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              aria-current={isActive(link.path) ? 'page' : undefined}
              className={`text-sm font-medium tracking-wide transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                isActive(link.path)
                  ? scrolled ? 'text-[#1E3A8A]' : 'text-white border-b-2 border-white pb-0.5'
                  : scrolled ? 'text-gray-600 hover:text-[#1E3A8A]' : 'text-white/80 hover:text-white'
              }`}
            >
              {link.label}
            </Link>
          ))}

          {/* Mehr-Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              aria-expanded={dropdownOpen}
              aria-haspopup="true"
              aria-label="Weitere Seiten anzeigen"
              className={`flex items-center gap-1 text-sm font-medium tracking-wide transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                isMoreActive
                  ? scrolled ? 'text-[#1E3A8A]' : 'text-white border-b-2 border-white pb-0.5'
                  : scrolled ? 'text-gray-600 hover:text-[#1E3A8A]' : 'text-white/80 hover:text-white'
              }`}
            >
              Mehr
              <span className="w-4 h-4 flex items-center justify-center" aria-hidden="true">
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </span>
            </button>

            {/* Dropdown Panel */}
            {dropdownOpen && (
              <div className="absolute top-full right-0 mt-3 w-52 bg-white rounded-xl border border-gray-100 overflow-hidden" role="menu">
                {moreLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    role="menuitem"
                    aria-current={isActive(link.path) ? 'page' : undefined}
                    className={`flex items-center gap-3 px-5 py-3.5 text-sm transition-colors duration-150 cursor-pointer ${
                      isActive(link.path)
                        ? 'bg-[#EFF6FF] text-[#1E3A8A] font-semibold'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-[#1E3A8A]'
                    }`}
                  >
                    <span className="w-5 h-5 flex items-center justify-center shrink-0" aria-hidden="true">
                      <i className={`${link.icon} text-[#1E3A8A]`}></i>
                    </span>
                    {link.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/shop"
            className={`flex items-center gap-1.5 text-sm font-medium tracking-wide transition-colors duration-200 whitespace-nowrap cursor-pointer ${
              isActive('/shop')
                ? scrolled ? 'text-[#1E3A8A]' : 'text-white border-b-2 border-white pb-0.5'
                : scrolled ? 'text-gray-600 hover:text-[#1E3A8A]' : 'text-white/80 hover:text-white'
            }`}
            aria-label="Shop"
          >
            <span className="w-4 h-4 flex items-center justify-center" aria-hidden="true">
              <i className="ri-store-2-line text-base"></i>
            </span>
            Shop
          </Link>

          <Link
            to="/anfrage"
            className="bg-[#1E3A8A] text-white text-sm font-semibold px-5 py-2.5 rounded-md hover:bg-[#1e40af] transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            Projekt anfragen
          </Link>
        </nav>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden flex flex-col gap-1.5 cursor-pointer p-2"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? 'Menü schließen' : 'Menü öffnen'}
          aria-expanded={menuOpen}
          aria-controls="mobile-menu"
        >
          <span className={`block w-6 h-0.5 transition-all duration-300 ${scrolled || menuOpen ? 'bg-gray-800' : 'bg-white'} ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} />
          <span className={`block w-6 h-0.5 transition-all duration-300 ${scrolled || menuOpen ? 'bg-gray-800' : 'bg-white'} ${menuOpen ? 'opacity-0' : ''}`} />
          <span className={`block w-6 h-0.5 transition-all duration-300 ${scrolled || menuOpen ? 'bg-gray-800' : 'bg-white'} ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <nav id="mobile-menu" className="md:hidden bg-white border-t border-gray-100 px-6 py-4 flex flex-col gap-1" aria-label="Mobile Navigation">
          {allLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              aria-current={isActive(link.path) ? 'page' : undefined}
              className={`flex items-center gap-3 text-sm font-medium py-3 px-3 rounded-lg cursor-pointer transition-colors ${
                isActive(link.path)
                  ? 'text-[#1E3A8A] bg-[#EFF6FF]'
                  : 'text-gray-700 hover:text-[#1E3A8A] hover:bg-gray-50'
              }`}
            >
              {'icon' in link && (
                <span className="w-4 h-4 flex items-center justify-center shrink-0" aria-hidden="true">
                  <i className={`${(link as typeof moreLinks[0]).icon} text-sm`}></i>
                </span>
              )}
              {link.label}
            </Link>
          ))}
          <div className="pt-2 mt-1 border-t border-gray-100 flex flex-col gap-2">
            <Link
              to="/shop"
              className={`flex items-center gap-3 text-sm font-medium py-3 px-3 rounded-lg cursor-pointer transition-colors ${
                isActive('/shop') ? 'text-[#1E3A8A] bg-[#EFF6FF]' : 'text-gray-700 hover:text-[#1E3A8A] hover:bg-gray-50'
              }`}
              aria-label="Shop"
            >
              <span className="w-4 h-4 flex items-center justify-center shrink-0" aria-hidden="true">
                <i className="ri-store-2-line text-sm"></i>
              </span>
              Shop
            </Link>
            <Link
              to="/anfrage"
              className="block bg-[#1E3A8A] text-white text-sm font-semibold px-5 py-3 rounded-md text-center whitespace-nowrap cursor-pointer hover:bg-[#1e40af] transition-colors"
            >
              Projekt anfragen
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
};

export default Navbar;
